import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Button, IconButton,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import dayjs from 'dayjs';
import api from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { useThemeMode } from '../../contexts/ThemeContext';
import { emptyForm, parseRecurrencePattern } from '../../utils/taskFormConstants';
import TaskFormContent from './TaskFormContent';

const SIDEBAR_WIDTH = 230;

const TaskFormDrawer = ({ open, onClose, onSuccess, editTask = null, anchor = 'right' }) => {
  const [formData, setFormData] = useState(emptyForm);
  const [editingTask, setEditingTask] = useState(null);
  const [isBonusMode, setIsBonusMode] = useState(false);
  const [iconPickerOpen, setIconPickerOpen] = useState(false);
  const [presetDrawerOpen, setPresetDrawerOpen] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const usersLoaded = useRef(false);

  const toast = useToast();
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';

  useEffect(() => {
    if (open && !usersLoaded.current) {
      api.getUsers().then(res => {
        setUsers(res.data.filter(u => u.type === 'child'));
        usersLoaded.current = true;
      }).catch(() => {});
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      if (editTask && editTask.id) {
        setEditingTask(editTask);
        const { recurrenceDays, recurrenceInterval, recurrenceEndType, recurrenceEndDate, recurrenceEndCount } = parseRecurrencePattern(editTask);
        setIsBonusMode(!!editTask.is_bonus);
        setFormData({
          title: editTask.title,
          description: editTask.description || '',
          assigned_to: editTask.assigned_to ? [editTask.assigned_to] : [],
          recurrence_type: editTask.recurrence_type,
          recurrence_days: recurrenceDays,
          reward_points: editTask.reward_points,
          cooldown_days: editTask.cooldown_days || 0,
          is_active: !!editTask.is_active,
          icon: editTask.icon || '',
          is_locked: !!editTask.is_locked,
          time_start: editTask.time_start || '',
          time_end: editTask.time_end || '',
          task_type: editTask.task_type || 'always',
          category: editTask.category || '',
          requires_approval: !!editTask.requires_approval,
          scheduled_date: editTask.scheduled_date || '',
          recurrence_interval: recurrenceInterval,
          recurrence_end_type: recurrenceEndType,
          recurrence_end_date: recurrenceEndDate,
          recurrence_end_count: recurrenceEndCount,
          max_claims: editTask.max_claims || '',
        });
      } else {
        setEditingTask(null);
        const isBonus = !!editTask?.is_bonus;
        setIsBonusMode(isBonus);
        setFormData({ ...emptyForm, reward_points: isBonus ? 5 : 1 });
      }
      setIconPickerOpen(false);
      setPresetDrawerOpen(false);
      setDatePickerOpen(false);
    }
  }, [open, editTask]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error('Tehtävän nimi on pakollinen');
      return;
    }
    if (formData.recurrence_type === 'custom' && formData.recurrence_days.length === 0) {
      toast.error('Valitse vähintään yksi päivä mukautetulle toistuvuudelle');
      return;
    }
    if (formData.recurrence_type === 'event' && !formData.scheduled_date) {
      toast.error('Valitse tapahtumapäivä');
      return;
    }

    const basePayload = {
      title: formData.title,
      description: formData.description,
      recurrence_type: formData.recurrence_type === 'once' && formData.scheduled_date && formData.scheduled_date !== dayjs().format('YYYY-MM-DD')
        ? 'event' : formData.recurrence_type,
      recurrence_pattern: formData.recurrence_type === 'custom'
        ? {
            days: formData.recurrence_days,
            ...(formData.recurrence_interval > 1 && { interval: formData.recurrence_interval }),
            ...(formData.recurrence_end_type !== 'never' && { end_type: formData.recurrence_end_type }),
            ...(formData.recurrence_end_type === 'on_date' && formData.recurrence_end_date && { end_date: formData.recurrence_end_date }),
            ...(formData.recurrence_end_type === 'after_count' && formData.recurrence_end_count && { end_count: parseInt(formData.recurrence_end_count) }),
          }
        : null,
      reward_points: formData.reward_points,
      cooldown_days: parseInt(formData.cooldown_days) || 0,
      is_active: formData.is_active,
      icon: formData.icon || null,
      is_locked: formData.is_locked,
      time_start: formData.time_start || null,
      time_end: formData.time_end || null,
      requires_approval: formData.requires_approval,
      ...(isBonusMode && { max_claims: formData.max_claims ? parseInt(formData.max_claims) : null }),
      task_type: isBonusMode ? 'bonus' : formData.task_type,
      category: formData.category || null,
      scheduled_date: formData.scheduled_date || null,
    };

    try {
      if (isBonusMode) {
        if (editingTask) {
          if (formData.assigned_to.length <= 1) {
            await api.put(`/bonus-tasks/${editingTask.id}`, {
              ...basePayload,
              assigned_to: formData.assigned_to.length === 1 ? formData.assigned_to[0] : null
            });
          } else {
            const [first, ...rest] = formData.assigned_to;
            await api.put(`/bonus-tasks/${editingTask.id}`, { ...basePayload, assigned_to: first });
            await Promise.all(rest.map(childId => api.post('/bonus-tasks', { ...basePayload, assigned_to: childId })));
          }
          toast.success('Bonustehtävä päivitetty');
        } else {
          if (formData.assigned_to.length === 0) {
            await api.post('/bonus-tasks', { ...basePayload, assigned_to: null });
            toast.success('Bonustehtävä luotu kaikille');
          } else if (formData.assigned_to.length === 1) {
            await api.post('/bonus-tasks', { ...basePayload, assigned_to: formData.assigned_to[0] });
            toast.success('Bonustehtävä luotu');
          } else {
            await Promise.all(formData.assigned_to.map(childId => api.post('/bonus-tasks', { ...basePayload, assigned_to: childId })));
            toast.success(`${formData.assigned_to.length} bonustehtävää luotu`);
          }
        }
      } else if (editingTask) {
        if (formData.assigned_to.length <= 1) {
          await api.updateTask(editingTask.id, {
            ...basePayload,
            assigned_to: formData.assigned_to.length === 1 ? formData.assigned_to[0] : null
          });
        } else {
          const [first, ...rest] = formData.assigned_to;
          await api.updateTask(editingTask.id, { ...basePayload, assigned_to: first });
          await Promise.all(rest.map(childId => api.createTask({ ...basePayload, assigned_to: childId })));
        }
        toast.success('Tehtävä päivitetty');
      } else {
        if (formData.assigned_to.length === 0) {
          await api.createTask({ ...basePayload, assigned_to: null });
          toast.success('Tehtävä luotu kaikille');
        } else if (formData.assigned_to.length === 1) {
          await api.createTask({ ...basePayload, assigned_to: formData.assigned_to[0] });
          toast.success('Tehtävä luotu');
        } else {
          await Promise.all(formData.assigned_to.map(childId => api.createTask({ ...basePayload, assigned_to: childId })));
          toast.success(`${formData.assigned_to.length} tehtävää luotu`);
        }
      }
      onClose();
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Tehtävän tallennus epäonnistui');
    }
  };

  const formContent = (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle1" fontWeight={700}>
          {editingTask ? (isBonusMode ? 'Muokkaa bonustehtävää' : 'Muokkaa tehtävää') : (isBonusMode ? 'Lisää bonustehtävä' : 'Luo uusi tehtävä')}
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 2 }}>
        <TaskFormContent
          formData={formData}
          setFormData={setFormData}
          users={users}
          editingTask={editingTask}
          isBonusMode={isBonusMode}
          setIsBonusMode={setIsBonusMode}
          iconPickerOpen={iconPickerOpen}
          setIconPickerOpen={setIconPickerOpen}
          presetDrawerOpen={presetDrawerOpen}
          setPresetDrawerOpen={setPresetDrawerOpen}
          datePickerOpen={datePickerOpen}
          setDatePickerOpen={setDatePickerOpen}
          isDark={isDark}
        />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, px: 2, py: 1.5, pb: { xs: 'calc(1.5rem + 64px + env(safe-area-inset-bottom, 0px))', sm: 1.5 }, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button onClick={onClose} size="small" sx={{ borderRadius: 2, px: 2.5 }}>Peruuta</Button>
        <Button type="submit" variant="contained" size="small" sx={{ borderRadius: 2, px: 2.5 }}>
          {editingTask ? 'Tallenna' : (isBonusMode ? 'Luo bonustehtävä' : 'Luo tehtävä')}
        </Button>
      </Box>
    </form>
  );

  if (anchor === 'right') {
    return (
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={onClose}
              style={{ position: 'fixed', inset: 0, zIndex: 1198 }}
            />
            <motion.div
              initial={{ x: 400 }}
              animate={{ x: 0 }}
              exit={{ x: 400 }}
              transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
              style={{
                position: 'fixed',
                top: 0,
                right: 0,
                width: 400,
                maxWidth: '100vw',
                height: '100vh',
                zIndex: 1199,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box sx={{
                width: '100%',
                height: '100%',
                bgcolor: isDark ? 'background.default' : 'background.paper',
                borderRadius: { xs: 0, sm: '16px 0 0 16px' },
                display: 'flex',
                flexDirection: 'column',
                overflow: 'auto',
              }}>
                {formContent}
              </Box>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    );
  }

  // anchor === 'left': framer-motion panel (same pattern as viestit)
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            style={{ position: 'fixed', inset: 0, zIndex: 1198 }}
          />
          <motion.div
            initial={{ x: -360 }}
            animate={{ x: 0 }}
            exit={{ x: -360 }}
            transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
            style={{
              position: 'fixed',
              top: 0,
              left: SIDEBAR_WIDTH,
              width: 360,
              height: '100vh',
              zIndex: 1199,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box sx={{
              height: '100%',
              bgcolor: 'background.paper',
              borderRight: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: 6,
            }}>
              {formContent}
            </Box>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default TaskFormDrawer;
